import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import classes from './profile.module.css';
import Header from '../../components/standartComponents/header';
import { useTranslation } from 'react-i18next';
import { logOut } from '../../lib/logOut';
import { getCSRFToken } from '../../lib/csrf';

const Profile = ({ authUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'profile');
  const [activeModal, setActiveModal] = useState(null);

  // Real user data from authUser (populated by checkAuthStatus -> allauth session)
  const userName = authUser?.name || authUser?.user?.display || authUser?.user?.username || 'User';
  const userEmail = authUser?.email || authUser?.user?.email || '';
  const userInitial = userName.charAt(0).toUpperCase();

  // Edit profile form state
  const [editName, setEditName] = useState('');
  const [editLanguage, setEditLanguage] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  // Change password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passSaving, setPassSaving] = useState(false);
  const [passError, setPassError] = useState('');
  const [passSuccess, setPassSuccess] = useState('');
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  // Subscription data from authUser stats or subscription object
  const subDaysRemaining = authUser?.stats?.subscription_remaining_days || 0;
  const subObj = authUser?.subscription;
  const subIsActive = subDaysRemaining > 0 || subObj?.status === 'active' || subObj?.status === 'trialing' || Number(subObj?.status) === 2;
  const hasSub = subIsActive;

  const [supportSent, setSupportSent] = useState(false);

  // Language code to display name mapping
  const langMap = { en: 'English', uk: 'Українська', pl: 'Polski' };
  const currentLangDisplay = langMap[i18n.language] || i18n.language;

  // Update active tab if location state changes
  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
    }
  }, [location.state]);

  const openMod = (id) => {
    setActiveModal(id);
    setEditError('');
    setEditSuccess('');
    setPassError('');
    setPassSuccess('');
    // Pre-fill edit form with current values
    if (id === 'editProfile') {
      setEditName(userName);
      setEditLanguage(i18n.language);
    }
    if (id === 'changePass') {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrentPass(false);
      setShowNewPass(false);
      setShowConfirmPass(false);
    }
  };
  const closeMod = () => setActiveModal(null);

  // Save profile changes (language via user-profile API)
  const saveProfile = async () => {
    setEditSaving(true);
    setEditError('');
    setEditSuccess('');

    try {
      const payload = {};
      let needsUpdate = false;
      
      if (editLanguage !== i18n.language) {
        payload.language = editLanguage;
        needsUpdate = true;
      }
      
      if (editName !== userName) {
        payload.name = editName; // The backend developer should have added this
        payload.first_name = editName; // Fallback just in case
        needsUpdate = true;
      }

      if (needsUpdate) {
        const csrfToken = getCSRFToken();
        await axios.patch('/api/v1/user-profile/current', 
          payload,
          { 
            withCredentials: true,
            headers: { 'X-CSRFToken': csrfToken, 'Content-Type': 'application/json' }
          }
        );
        if (payload.language) {
          i18n.changeLanguage(payload.language);
          localStorage.setItem('selectedLanguage', payload.language);
        }
        // Optimistically update authUser name so UI reflects change immediately
        if (payload.name) {
          authUser.name = editName;
          if (authUser.user) {
            authUser.user.display = editName;
            authUser.user.first_name = editName;
          }
        }
        // Also try refreshing from server (may return old data if backend caches)
        if (window.refreshAuthUser) {
          window.refreshAuthUser();
        }
      }

      setEditSuccess(t('Profile updated successfully') || 'Профіль оновлено!');
      // Close modal after brief success display
      setTimeout(() => {
        closeMod();
      }, 1200);
    } catch (error) {
      console.error('Error saving profile:', error);
      setEditError(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        t('Error saving profile') || 'Помилка збереження профілю'
      );
    } finally {
      setEditSaving(false);
    }
  };

  // Change password via allauth
  const changePassword = async () => {
    setPassError('');
    setPassSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPassError(t('Please fill in all fields') || 'Заповніть усі поля');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPassError(t('Passwords do not match') || 'Паролі не збігаються');
      return;
    }
    if (newPassword.length < 8) {
      setPassError(t('Password must be at least 8 characters') || 'Пароль має бути не менше 8 символів');
      return;
    }

    setPassSaving(true);
    try {
      const csrfToken = getCSRFToken();
      await axios.post('/api/_allauth/browser/v1/account/password/change', 
        { current_password: currentPassword, new_password: newPassword },
        { 
          withCredentials: true,
          headers: { 'X-CSRFToken': csrfToken, 'Content-Type': 'application/json' }
        }
      );
      setPassSuccess(t('Password changed successfully') || 'Пароль змінено!');
      setTimeout(() => {
        closeMod();
      }, 1200);
    } catch (error) {
      console.error('Error changing password:', error);
      const errData = error.response?.data;
      // allauth returns errors in different formats
      if (errData?.errors) {
        setPassError(errData.errors.map(e => e.message).join('. '));
      } else if (errData?.current_password) {
        setPassError(errData.current_password.join('. '));
      } else {
        setPassError(
          errData?.detail || 
          errData?.message || 
          t('Error changing password') || 'Помилка зміни паролю'
        );
      }
    } finally {
      setPassSaving(false);
    }
  };

  const submitSupport = () => {
    setSupportSent(true);
  };

  const doLogout = async () => {
    closeMod();
    const success = await logOut();
    if (success) {
      window.location.href = '/';
    }
  };

  return (
    <>
      <Header authUser={authUser} />
      <div className={classes.main}>
      {/* SIDEBAR */}
      <div className={classes.sidebar}>
        <button className={`${classes.sbItem} ${activeTab === 'profile' ? classes.on : ''}`} onClick={() => setActiveTab('profile')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>{t('My profile') || 'Мій профіль'}
        </button>
        <button className={`${classes.sbItem} ${activeTab === 'sub' ? classes.on : ''}`} onClick={() => setActiveTab('sub')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>{t('Subscription') || 'Підписка'}
        </button>
        <button className={`${classes.sbItem} ${activeTab === 'support' ? classes.on : ''}`} onClick={() => setActiveTab('support')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>{t('Support') || 'Підтримка'}
        </button>
        <div className={classes.sbSep}></div>
        <button className={`${classes.sbItem} ${classes.logout}`} onClick={() => openMod('logout')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>{t('Log Out') || 'Вийти'}
        </button>
      </div>

      {/* CONTENT */}
      <div className={classes.content}>
        <h1 className={classes.pageTitle}>{t('Personal cabinet') || 'Персональний кабінет'}</h1>
        <p className={classes.pageSub}>{t('Manage your profile, subscription and support requests') || 'Керуйте профілем, підпискою та зверненнями до підтримки.'}</p>

        {/* PROFILE TAB */}
        <div className={`${classes.section} ${activeTab === 'profile' ? classes.active : ''}`}>
          <div className={classes.card}>
            <div className={classes.profileHeader}>
              <div className={classes.profileAv}>{userInitial}</div>
              <div><div className={classes.profileName}>{userName}</div><div className={classes.profileEmail}>{userEmail}</div></div>
            </div>
            <div className={classes.infoGrid}>
              <div className={classes.infoItem}><div className={classes.infoLabel}>{t('Name') || "Ім'я"}</div><div className={classes.infoValue}>{userName}</div></div>
              <div className={classes.infoItem}><div className={classes.infoLabel}>Email</div><div className={classes.infoValue}>{userEmail || '—'}</div></div>
              <div className={classes.infoItem}><div className={classes.infoLabel}>{t('Interface language') || 'Мова інтерфейсу'}</div><div className={classes.infoValue}>{currentLangDisplay}</div></div>
              <div className={classes.infoItem}><div className={classes.infoLabel}>{t('English level') || 'Рівень англійської'}</div><div className={classes.infoValue}>{authUser?.user?.level || '—'}</div></div>
            </div>
            <div className={classes.btnRow}>
              <button className={`${classes.btn} ${classes.btnP}`} onClick={() => openMod('editProfile')}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>{t('Edit profile') || 'Редагувати профіль'}</button>
              <button className={`${classes.btn} ${classes.btnG}`} onClick={() => openMod('changePass')}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>{t('Change password') || 'Змінити пароль'}</button>
            </div>
          </div>
        </div>

        {/* SUBSCRIPTION TAB */}
        <div className={`${classes.section} ${activeTab === 'sub' ? classes.active : ''}`}>
          <div className={classes.card}>
            <div className={classes.cardTitle}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>{t('Subscription') || 'Підписка'}</div>
            
            {hasSub ? (
              <div className={`${classes.subBadge} ${classes.active}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>{t('Active') || 'Активна'}</div>
            ) : (
              <div className={`${classes.subBadge} ${classes.cancelled}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>{t('No active subscription') || 'Немає активної підписки'}</div>
            )}
            
            {hasSub && (
              <div className={classes.subGrid}>
                {(subDaysRemaining > 0) && (
                  <div className={classes.subItem}><div className={classes.subLabel}>{t('days remaining') || 'Днів залишилось'}</div><div className={classes.subValue}>{subDaysRemaining || subObj?.remaining_days || subObj?.days_remaining || subObj?.days_left || '—'}</div></div>
                )}
                {(authUser?.stats?.subscription_plan || subObj?.plan || subObj?.plan_name || subObj?.tariff_name) && (
                  <div className={classes.subItem}><div className={classes.subLabel}>{t('Plan') || 'Тариф'}</div><div className={classes.subValue}>{authUser?.stats?.subscription_plan || subObj?.plan || subObj?.plan_name || subObj?.tariff_name}</div></div>
                )}
              </div>
            )}
            
            <div className={classes.btnRow}>
              <button className={`${classes.btn} ${classes.btnP}`} onClick={() => navigate('/pricing')}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>{hasSub ? (t('Upgrade plan') || 'Оновити тариф') : (t('Get a plan') || 'Обрати тариф')}</button>
            </div>
          </div>
        </div>

        {/* SUPPORT TAB */}
        <div className={`${classes.section} ${activeTab === 'support' ? classes.active : ''}`}>
          <div className={classes.card}>
            <div className={classes.cardTitle}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>{t('Support') || 'Підтримка'}</div>
            <p style={{fontSize: '14px', color: 'var(--text-2)', marginBottom: '16px'}}>{t('Write to us if you have questions about lessons, subscription or technical issues') || 'Напишіть нам, якщо маєте питання щодо уроків, підписки або технічної проблеми.'}</p>
            <div className={classes.supportEmail}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>ailinguaservice@gmail.com</div>
            
            {!supportSent ? (
              <div>
                <div className={classes.fg}><label className={classes.fl}>{t('Subject') || 'Тема звернення'}</label><select className={classes.fi}><option>{t('Subscription question') || 'Питання щодо підписки'}</option><option>{t('Payment issue') || 'Проблема з оплатою'}</option><option>{t('Lesson issue') || 'Проблема з уроком'}</option><option>{t('Technical issue') || 'Технічна проблема'}</option><option>{t('Other') || 'Інше'}</option></select></div>
                <div className={classes.fg}><label className={classes.fl}>{t('Message') || 'Повідомлення'}</label><textarea className={classes.fi} placeholder={t('Describe your question or issue') || 'Опишіть ваше питання або проблему…'}></textarea></div>
                <button className={`${classes.btn} ${classes.btnP}`} onClick={submitSupport}>{t('Send') || 'Надіслати'}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg></button>
              </div>
            ) : (
              <div className={`${classes.successBox} ${classes.show}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                <div className={classes.st}>{t('Thank you') || 'Дякуємо!'}</div>
                <div className={classes.ss}>{t('We received your request and will reply to your email') || 'Ми отримали ваше звернення і відповімо на вашу пошту.'}</div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* MODALS */}
      {/* Edit Profile */}
      <div className={`${classes.ov} ${activeModal === 'editProfile' ? classes.show : ''}`} onClick={closeMod}>
        <div className={classes.mod} onClick={e => e.stopPropagation()}>
          <div className={classes.modT}>{t('Edit profile') || 'Редагувати профіль'}</div>
          <div className={classes.modS}>{t('Update your personal data') || 'Оновіть ваші персональні дані.'}</div>
          
          <div className={classes.fg}>
            <label className={classes.fl}>{t('Name') || "Ім'я"}</label>
            <input className={classes.fi} value={editName} onChange={e => setEditName(e.target.value)} />
          </div>
          <div className={classes.fg}>
            <label className={classes.fl}>Email</label>
            <input className={classes.fi} value={userEmail} disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />
            <small style={{ color: '#94a3b8', fontSize: '12px', marginTop: '4px' }}>{t('Email cannot be changed') || 'Email не може бути змінений'}</small>
          </div>
          <div className={classes.fg}>
            <label className={classes.fl}>{t('Interface language') || 'Мова інтерфейсу'}</label>
            <select className={classes.fi} value={editLanguage} onChange={e => setEditLanguage(e.target.value)}>
              <option value="uk">Українська</option>
              <option value="en">English</option>
              <option value="pl">Polski</option>
            </select>
          </div>

          {editError && <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px', padding: '8px 12px', background: '#fef2f2', borderRadius: '8px' }}>{editError}</div>}
          {editSuccess && <div style={{ color: '#22c55e', fontSize: '14px', marginTop: '8px', padding: '8px 12px', background: '#f0fdf4', borderRadius: '8px' }}>{editSuccess}</div>}

          <div className={classes.modBtns}>
            <button className={`${classes.btn} ${classes.btnG}`} onClick={closeMod} disabled={editSaving}>{t('Cancel') || 'Скасувати'}</button>
            <button className={`${classes.btn} ${classes.btnP}`} onClick={saveProfile} disabled={editSaving}>
              {editSaving ? (t('Saving...') || 'Збереження...') : (t('Save') || 'Зберегти')}
            </button>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className={`${classes.ov} ${activeModal === 'changePass' ? classes.show : ''}`} onClick={closeMod}>
        <div className={classes.mod} onClick={e => e.stopPropagation()}>
          <div className={classes.modT}>{t('Change password') || 'Змінити пароль'}</div>
          <div className={classes.modS}>{t('Enter your current and new password') || 'Введіть поточний і новий пароль.'}</div>
          <div className={classes.fg}>
            <label className={classes.fl}>{t('Current password') || 'Поточний пароль'}</label>
            <div className={classes.passWrap}>
              <input className={classes.fi} type={showCurrentPass ? 'text' : 'password'} placeholder="••••••••" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
              <button type="button" className={classes.passToggle} onClick={() => setShowCurrentPass(p => !p)} tabIndex={-1}>
                {showCurrentPass ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" x2="23" y1="1" y2="23"/></svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>
          <div className={classes.fg}>
            <label className={classes.fl}>{t('New password') || 'Новий пароль'}</label>
            <div className={classes.passWrap}>
              <input className={classes.fi} type={showNewPass ? 'text' : 'password'} placeholder="••••••••" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              <button type="button" className={classes.passToggle} onClick={() => setShowNewPass(p => !p)} tabIndex={-1}>
                {showNewPass ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" x2="23" y1="1" y2="23"/></svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>
          <div className={classes.fg}>
            <label className={classes.fl}>{t('Confirm new password') || 'Повторіть новий пароль'}</label>
            <div className={classes.passWrap}>
              <input className={classes.fi} type={showConfirmPass ? 'text' : 'password'} placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
              <button type="button" className={classes.passToggle} onClick={() => setShowConfirmPass(p => !p)} tabIndex={-1}>
                {showConfirmPass ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" x2="23" y1="1" y2="23"/></svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          {passError && <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px', padding: '8px 12px', background: '#fef2f2', borderRadius: '8px' }}>{passError}</div>}
          {passSuccess && <div style={{ color: '#22c55e', fontSize: '14px', marginTop: '8px', padding: '8px 12px', background: '#f0fdf4', borderRadius: '8px' }}>{passSuccess}</div>}

          <div className={classes.modBtns}>
            <button className={`${classes.btn} ${classes.btnG}`} onClick={closeMod} disabled={passSaving}>{t('Cancel') || 'Скасувати'}</button>
            <button className={`${classes.btn} ${classes.btnP}`} onClick={changePassword} disabled={passSaving}>
              {passSaving ? (t('Saving...') || 'Збереження...') : (t('Update password') || 'Оновити пароль')}
            </button>
          </div>
        </div>
      </div>

      {/* Logout */}
      <div className={`${classes.ov} ${activeModal === 'logout' ? classes.show : ''}`} onClick={closeMod}>
        <div className={classes.mod} onClick={e => e.stopPropagation()}>
          <div className={`${classes.modIcon} ${classes.modIconDanger}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg></div>
          <div className={classes.modT}>{t('Log out of account?') || 'Вийти з акаунта?'}</div>
          <div className={classes.modS}>{t('You can return to learning after logging in again') || 'Ви зможете повернутися до навчання після повторного входу.'}</div>
          <div className={classes.modBtns}>
            <button className={`${classes.btn} ${classes.btnG}`} onClick={closeMod}>{t('Cancel') || 'Скасувати'}</button>
            <button className={`${classes.btn} ${classes.btnR}`} onClick={doLogout}>{t('Log Out') || 'Вийти'}</button>
          </div>
        </div>
      </div>
      
      </div>
    </>
  );
};

export default Profile;
